import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useCallback, useState } from "react";
import { getOrgIdForFetchExternalWorklist } from "../../../utils/organizationHelper";
import { addSupportTicketComment } from "../apis/addSupportTicketComment";
import { uploadSupportTicketAttachmentToStorage } from "../utils/uploadSupportTicketAttachment";

/**
 * Options for {@link useAddSupportTicketComment}.
 */
export interface UseAddSupportTicketCommentOptions {
  /** Currently selected support ticket id. */
  ticketId: string;
  /** Called after a comment is added successfully. */
  onSuccess?: () => void | Promise<void>;
}

/**
 * Return value for {@link useAddSupportTicketComment}.
 */
export interface UseAddSupportTicketCommentReturn {
  /**
   * Submits a comment (and optional attachments) for the selected ticket.
   * Uploads each file to Firebase Storage first, then posts them with the
   * comment. Returns true on success.
   */
  addComment: (message: string, files?: File[]) => Promise<boolean>;
  /** Whether the add-comment request (including uploads) is in flight. */
  isAddingComment: boolean;
}

/**
 * Handles add-comment submission with toast feedback for the support tickets page.
 *
 * @param options - Selected ticket id and optional success callback.
 * @returns Async add-comment handler and submitting flag.
 */
export function useAddSupportTicketComment(
  options: UseAddSupportTicketCommentOptions,
): UseAddSupportTicketCommentReturn {
  const { ticketId, onSuccess } = options;
  const [isAddingComment, setIsAddingComment] = useState(false);

  const addComment = useCallback(
    async (message: string, files: File[] = []): Promise<boolean> => {
      const trimmedMessage = message.trim();
      if (!trimmedMessage && files.length === 0) {
        return false;
      }

      const orgId = getOrgIdForFetchExternalWorklist();
      if (!orgId) {
        toast.error("Organization id is unavailable.");
        return false;
      }

      if (!ticketId) {
        toast.error("Select a support ticket before adding a comment.");
        return false;
      }

      setIsAddingComment(true);

      try {
        const uploaded = await Promise.all(
          files.map((file) =>
            uploadSupportTicketAttachmentToStorage(file, orgId, ticketId),
          ),
        );
        await addSupportTicketComment({
          org_id: orgId,
          ticket_id: ticketId,
          message: trimmedMessage,
          attachments: uploaded,
        });
        toast.success("Comment added successfully.");
        await onSuccess?.();
        return true;
      } catch (error) {
        toast.error(
          error instanceof Error ? error.message : "Failed to add comment.",
        );
        return false;
      } finally {
        setIsAddingComment(false);
      }
    },
    [onSuccess, ticketId],
  );

  return {
    addComment,
    isAddingComment,
  };
}
